import type { CardPrintTemplate } from '@citizen-walk/shared-types';
import { CARD_FONT_LIST, DEFAULT_CARD_FONT_ID, resolveCardFontId } from './fontRegistry';

/**
 * Card-print 視覚テーマ helper。
 *
 * 旧 `old2/apps/admin/src/features/cardPrint/{types,themes}.ts` の `CardTheme` を
 * v2 の `ResolvedCardTheme` 形式に移植 (AW6 — old2 parity)。
 *
 * 永続化単位: M7 `cardPrintTemplates.theme` (template 単位、地区横断)。
 *
 * ## 永続化と client-resolved の分離 (重要)
 * shared-types `CardPrintTemplate.theme` の schema は backend と整合する必要があり、
 * 本 sprint では backend / rules 変更を行わない。そのため M7 master へ書き戻す `themeToPayload`
 * は schema が持つフィールド (`backgroundColor` / `accent` / `textColor` / `fontFamily` /
 * `logoUrl` / `decorations`) のみを書き出す。
 *
 * 一方 ResolvedCardTheme は old2 同等の表現力 (secondary / 見出し色・本文色分離 / categoryColors /
 * border / layout / decoration の opacity・rotation・secondary tint) を **client 側でのみ** 保持する。
 * これにより印刷ジョブ / preset 適用時は old2 と同じ密度で描画できるが、master 永続化時は
 * schema subset に丸める (差分は preset 再適用で復元)。Phase 5 後続で theme schema 拡張時に
 * `themeToPayload` の出力を広げれば完全永続化に移行できる。
 */

export type DecorationSlot =
  | 'background-pattern'
  | 'header-band'
  | 'footer-band'
  | 'left-side-band'
  | 'corner-top-left'
  | 'corner-top-right'
  | 'corner-bottom-left'
  | 'corner-bottom-right'
  | 'title-icon';

export type DecorationTint = 'primary' | 'secondary' | 'accent' | 'none';

/**
 * Client-resolved decoration。M7 schema の decoration は `tintWith: 'accent' | 'none'` のみだが、
 * client では old2 互換で `primary` (= accent と同義) / `secondary` / `opacity` / `rotation_deg`
 * も扱う。M7 へ書き戻す際は `themeToPayload` が schema subset (`accent` / `none`) に丸める。
 */
export interface Decoration {
  slot: DecorationSlot;
  svgSource: string;
  x_mm: number;
  y_mm: number;
  width_mm: number;
  height_mm: number;
  opacity?: number;
  rotation_deg?: number;
  tintWith?: DecorationTint;
}

export interface CardThemeBorder {
  color: string;
  width_mm: number;
}

export interface CardThemeLayout {
  photoSide: 'left' | 'right';
  photoWidth_mm: number;
  textPadding_mm: number;
  titleFontSize_pt: number;
  bodyFontSize_pt: number;
}

export interface ResolvedCardTheme {
  backgroundColor: string;
  /** メイン色 (old2 `primary`)。`accent` 名で従来通り露出。 */
  accent: string;
  /** 副色 (old2 `secondary`)。装飾の `secondary` tint / カテゴリ既定色に使用。 */
  secondary: string;
  /** 見出し (タイトル) 文字色 (old2 `textHeading`)。 */
  textHeading: string;
  /** 本文文字色 (old2 `textBody`)。`textColor` の別名でもある (後方互換)。 */
  textColor: string;
  fontFamily: string;
  logoUrl: string;
  /** カテゴリラベル → 表示色 (old2 `categoryColors`、admin DataSettings の動的 label キー)。 */
  categoryColors: Record<string, string>;
  /** カード枠線 (old2 `border`)。width_mm=0 で非表示。 */
  border: CardThemeBorder;
  /** 写真位置・寸法・フォントサイズ (old2 `layout`、寸法ハードコード解消)。 */
  layout: CardThemeLayout;
  decorations: Decoration[];
}

export const DEFAULT_CARD_LAYOUT: CardThemeLayout = {
  photoSide: 'left',
  photoWidth_mm: 40,
  textPadding_mm: 2,
  titleFontSize_pt: 10,
  bodyFontSize_pt: 8,
};

export const DEFAULT_CARD_THEME: ResolvedCardTheme = {
  backgroundColor: '#ffffff',
  accent: '#2563eb',
  secondary: '#64748b',
  textHeading: '#0f172a',
  textColor: '#0f172a',
  fontFamily: DEFAULT_CARD_FONT_ID,
  logoUrl: '',
  categoryColors: {},
  border: { color: '#e2e8f0', width_mm: 0 },
  layout: { ...DEFAULT_CARD_LAYOUT },
  decorations: [],
};

// フォント選択肢は「PDF に埋め込める日本語フォント」(fontRegistry) のみ。
// ユーザーごとに異なるシステムフォントは使わない (WYSIWYG 保証)。値は font id。
export const FONT_FAMILY_OPTIONS: { value: string; label: string }[] = CARD_FONT_LIST.map((f) => ({
  value: f.id,
  label: f.label,
}));

export const DECORATION_SLOT_LABELS: Record<DecorationSlot, string> = {
  'background-pattern': '背景パターン',
  'header-band': '上部帯',
  'footer-band': '下部帯',
  'left-side-band': '左縦バンド',
  'corner-top-left': '左上角',
  'corner-top-right': '右上角',
  'corner-bottom-left': '左下角',
  'corner-bottom-right': '右下角',
  'title-icon': 'タイトル横アイコン',
};

/**
 * M7 template の `theme?` を resolved CardTheme に正規化。
 * 未設定 / 部分指定 / 不正な値は DEFAULT_CARD_THEME で補完。
 *
 * schema に無い rich フィールド (secondary / textHeading / categoryColors / border / layout) は
 * default で埋める。preset 適用後にこれらが上書きされる想定。
 */
export function resolveCardTheme(
  partial: CardPrintTemplate['theme'] | null | undefined,
): ResolvedCardTheme {
  if (!partial) return cloneTheme(DEFAULT_CARD_THEME);
  const textColor = normalizeColor(partial.textColor, DEFAULT_CARD_THEME.textColor);
  return {
    backgroundColor: normalizeColor(partial.backgroundColor, DEFAULT_CARD_THEME.backgroundColor),
    accent: normalizeColor(partial.accent, DEFAULT_CARD_THEME.accent),
    secondary: DEFAULT_CARD_THEME.secondary,
    // schema は textColor 単一。見出し色は本文色から派生 (preset で分離指定)。
    textHeading: textColor,
    textColor,
    // fontFamily は font id ('sans'/'serif'/'mono')。旧 CSS 文字列も resolveCardFontId が吸収。
    fontFamily: resolveCardFontId(partial.fontFamily),
    logoUrl:
      typeof partial.logoUrl === 'string' && /^https?:\/\//.test(partial.logoUrl)
        ? partial.logoUrl
        : '',
    categoryColors: {},
    border: { ...DEFAULT_CARD_THEME.border },
    layout: { ...DEFAULT_CARD_LAYOUT },
    decorations: Array.isArray(partial.decorations)
      ? partial.decorations.map(normalizeDecoration)
      : [],
  };
}

/**
 * resolved theme → M7 payload (theme サブ object、schema subset)。
 * default 値と一致するフィールドは省略 (Firestore 書込容量削減 + diff の明瞭化)。
 * decorations 配列は空でも空配列で残す (明示的 clear を可能にする)。
 *
 * schema に無い secondary / textHeading / categoryColors / border / layout は永続化されない
 * (cardTheme.ts のヘッダ note 参照)。decoration の tintWith は `secondary`/`primary` を
 * `accent`/`none` に丸める。
 */
export function themeToPayload(theme: ResolvedCardTheme): NonNullable<CardPrintTemplate['theme']> {
  const out: NonNullable<CardPrintTemplate['theme']> = {};
  if (theme.backgroundColor !== DEFAULT_CARD_THEME.backgroundColor)
    out.backgroundColor = theme.backgroundColor;
  if (theme.accent !== DEFAULT_CARD_THEME.accent) out.accent = theme.accent;
  if (theme.textColor !== DEFAULT_CARD_THEME.textColor) out.textColor = theme.textColor;
  if (theme.fontFamily !== DEFAULT_CARD_THEME.fontFamily) out.fontFamily = theme.fontFamily;
  if (theme.logoUrl) out.logoUrl = theme.logoUrl;
  if (theme.decorations.length > 0) {
    out.decorations = theme.decorations.map((d) => ({
      slot: d.slot,
      svgSource: d.svgSource,
      x_mm: d.x_mm,
      y_mm: d.y_mm,
      width_mm: d.width_mm,
      height_mm: d.height_mm,
      tintWith:
        d.tintWith === 'accent' || d.tintWith === 'primary' || d.tintWith === 'secondary'
          ? 'accent'
          : 'none',
    }));
  }
  return out;
}

/** deep clone (preset 適用時に共有参照を避ける)。 */
export function cloneTheme(theme: ResolvedCardTheme): ResolvedCardTheme {
  return {
    ...theme,
    categoryColors: { ...theme.categoryColors },
    border: { ...theme.border },
    layout: { ...theme.layout },
    decorations: theme.decorations.map((d) => ({ ...d })),
  };
}

function normalizeDecoration(d: Decoration): Decoration {
  return {
    slot: d.slot,
    svgSource: d.svgSource,
    x_mm: d.x_mm,
    y_mm: d.y_mm,
    width_mm: d.width_mm,
    height_mm: d.height_mm,
    opacity: typeof d.opacity === 'number' ? d.opacity : undefined,
    rotation_deg: typeof d.rotation_deg === 'number' ? d.rotation_deg : undefined,
    tintWith: d.tintWith ?? 'none',
  };
}

function normalizeColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  // #RGB or #RRGGBB のみ受理
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim())) return value.trim();
  return fallback;
}
